import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { THEME, flexxState } from "./state";
import SessionsClock from "./SessionsClock";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AXEL Daily Briefing — gold-bordered card on the Overview dashboard.
 * Generates a personalized 4-line morning message based on:
 *   - Tier / track
 *   - Streak
 *   - Last logged trade grade
 *   - Today's market context (session-based)
 *
 * Strategy: template-based personalization for instant load (no LLM cost
 * per page view). "Refresh briefing" button calls real AXEL via /api/axel/chat
 * for a one-off fresh take when the trader wants more depth.
 */
export default function AxelDailyBriefing() {
  const [refreshedMsg, setRefreshedMsg] = useState(() => loadCachedRefresh());
  const [loading, setLoading] = useState(false);
  const [, setTick] = useState(0);

  // ── ElevenLabs voice narration ──────────────────────────────
  const [ttsOn, setTtsOn] = useState(false);
  const [ttsState, setTtsState] = useState("idle"); // idle | loading | playing | paused
  const audioRef = useRef(null);
  const audioTextRef = useRef("");

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("flexx_access_token");
        if (!token || !API) return;
        const res = await axios.get(`${API}/api/tts/status`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        });
        setTtsOn(!!res.data?.configured);
      } catch (e) {
        setTtsOn(false);
      }
    })();
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const listen = async () => {
    // Pause if currently playing.
    if (ttsState === "playing" && audioRef.current) {
      audioRef.current.pause();
      setTtsState("paused");
      return;
    }
    // Resume if we already have audio for the current text.
    if (ttsState === "paused" && audioRef.current && audioTextRef.current === display) {
      audioRef.current.play();
      setTtsState("playing");
      return;
    }
    setTtsState("loading");
    try {
      const token = localStorage.getItem("flexx_access_token");
      const res = await axios.post(
        `${API}/api/tts/speak`,
        { text: display },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 40000 }
      );
      const url = res.data?.audio_url;
      if (!url) throw new Error("no audio");
      if (audioRef.current) audioRef.current.pause();
      const audio = new Audio(url);
      audio.onended = () => setTtsState("idle");
      audio.onpause = () => setTtsState((s) => (s === "playing" ? "paused" : s));
      audioRef.current = audio;
      audioTextRef.current = display;
      await audio.play();
      setTtsState("playing");
    } catch (e) {
      setTtsState("idle");
    }
  };

  useEffect(() => {
    const onRestored = () => setTick((t) => t + 1);
    window.addEventListener("flexx:restored", onRestored);
    return () => window.removeEventListener("flexx:restored", onRestored);
  }, []);

  const briefing = useMemo(() => buildLocalBriefing(), []);

  const refresh = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("flexx_access_token");
      if (!token || !API) return;
      const ctx = buildContextSummary();
      const prompt = `Give me a daily briefing in EXACTLY 4 short lines using AXEL voice.
Line 1: a direct greeting + 1 fact about my recent activity
Line 2: a 1-sentence focus theme for today based on SuPER step I should drill
Line 3: a market context note for the current session
Line 4: one specific ACTION ITEM I can do today (under 10 words)
NO bullets. NO emojis except 🎯 at start of line 4. NO preamble.

Context:
${ctx}`;
      const res = await axios.post(
        `${API}/api/axel/chat`,
        { session_id: `daily-briefing-${new Date().toISOString().slice(0, 10)}`, message: prompt, reset: true },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 25000 }
      );
      const msg = res.data?.response;
      if (msg) {
        setRefreshedMsg(msg);
        cacheRefresh(msg);
      }
    } catch (e) {
      // silent fail — keep template briefing
    } finally {
      setLoading(false);
    }
  };

  const display = refreshedMsg || briefing;

  return (
    <div
      data-testid="axel-daily-briefing"
      className="flexx-card flexx-fade-in"
      style={{
        marginBottom: 16,
        background: "linear-gradient(135deg, #0d0a04 0%, #15110a 100%)",
        border: `1px solid ${THEME.gold}50`,
        borderLeft: `3px solid ${THEME.gold}`,
        boxShadow: `0 0 24px rgba(201,162,39,0.10)`,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flex: 1, minWidth: 280 }}>
          <div
            style={{
              width: 44, height: 44, borderRadius: 22,
              background: `linear-gradient(135deg, ${THEME.gold}, #E8B94F)`,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#0a0a0a", fontWeight: 800, fontFamily: "monospace", fontSize: 14, flexShrink: 0,
            }}
          >
            AX
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
              <div style={{ fontSize: 9, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace" }}>
                AXEL — DAILY BRIEFING
              </div>
              <SessionsClock compact />
            </div>
            <div data-testid="axel-briefing-text" style={{ color: THEME.text, fontSize: 14, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
              {display}
            </div>
            <EconFlashpoints />
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
          {ttsOn && (
            <button
              data-testid="axel-briefing-listen-btn"
              onClick={listen}
              disabled={ttsState === "loading"}
              className="flexx-btn-ghost"
              style={{ fontSize: 11, padding: "6px 12px", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6 }}
              title="Listen to today's briefing"
            >
              <span aria-hidden style={{ fontSize: 12 }}>
                {ttsState === "playing" ? "⏸" : "🔊"}
              </span>
              {ttsState === "loading"
                ? "Loading…"
                : ttsState === "playing"
                ? "Pause"
                : ttsState === "paused"
                ? "Resume"
                : "Listen"}
            </button>
          )}
          <button
            data-testid="axel-briefing-refresh-btn"
            onClick={refresh}
            disabled={loading}
            className="flexx-btn-ghost"
            style={{ fontSize: 11, padding: "6px 12px", whiteSpace: "nowrap" }}
          >
            {loading ? "AXEL is thinking…" : "↻ Fresh take"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────

function buildLocalBriefing() {
  const profile = flexxState.profile();
  const streak = flexxState.streak();
  const journal = flexxState.journal();
  const lastTrade = journal[0] || null;
  const name = profile?.name?.split(" ")[0] || "trader";
  const session = currentSession();

  // Line 1 — greeting + recent activity fact
  let line1;
  if (streak.days >= 7) {
    line1 = `Morning, ${name}. ${streak.days}-day streak. That's discipline showing up.`;
  } else if (streak.days >= 3) {
    line1 = `Hey ${name}. ${streak.days} days in a row — consistency is forming. Don't break it.`;
  } else if (lastTrade) {
    line1 = `Welcome back, ${name}. Last trade: ${lastTrade.market} ${lastTrade.direction}${lastTrade.grade ? ` · grade ${lastTrade.grade}` : ""}.`;
  } else {
    line1 = `New session, ${name}. Let's set the tone.`;
  }

  // Line 2 — focus theme based on weakest SuPER step (heuristic from last 5 trades)
  const recent = journal.slice(0, 5).filter((e) => e.super);
  let focus;
  if (recent.length === 0) {
    focus = "Today's drill: log every trade with the full SuPER checklist. Process > profit.";
  } else {
    const avgU = recent.reduce((s, e) => s + (e.super?.uScore || 0), 0) / recent.length;
    if (avgU < 5) focus = "Today's drill: the 'u — Understand' step. 5+ YES before any plan.";
    else if (recent.some((e) => e.grade === "F")) focus = "Today's drill: rebuild discipline. No FOMO. No revenge. Plan or pass.";
    else if (recent.some((e) => e.grade === "D")) focus = "Today's drill: write the plan BEFORE the entry. Every. Single. Time.";
    else focus = "Today's drill: tighten the SL placement. Structure-based, never random.";
  }

  // Line 3 — market context based on current session
  const sessionLine = sessionContextLine(session, profile?.market);

  // Line 4 — action item
  const action = pickAction(streak, lastTrade);

  return `${line1}\n${focus}\n${sessionLine}\n${action}`;
}

function buildContextSummary() {
  const profile = flexxState.profile();
  const streak = flexxState.streak();
  const xpVal = flexxState.xp();
  const journal = flexxState.journal();
  const last3 = journal.slice(0, 3).map((e) => `${e.market} ${e.direction} ${e.result}${e.grade ? ` (grade ${e.grade})` : ""}`).join("; ");
  return `Name: ${profile?.name || "Unknown"}
Market: ${profile?.market || "Forex"}
Streak: ${streak.days} days
XP: ${xpVal || 0}
Last 3 trades: ${last3 || "none yet"}
Session: ${currentSession()}`;
}

function currentSession() {
  // Simple UTC-based session detector (not user-tz-aware for v1)
  const h = new Date().getUTCHours();
  if (h >= 0 && h < 7) return "Tokyo";
  if (h >= 7 && h < 12) return "London";
  if (h >= 12 && h < 16) return "Overlap (London/NY)";
  if (h >= 16 && h < 21) return "New York";
  return "Pacific (post-close)";
}

function sessionContextLine(session, market) {
  const isCrypto = (market || "").toLowerCase().includes("crypto");
  if (isCrypto) {
    if (session.includes("Tokyo")) return "Crypto · Tokyo session — usually thin liquidity. Wait for confirmation.";
    if (session.includes("London")) return "Crypto · London open — first volume wave. Watch BTC.D.";
    if (session.includes("Overlap")) return "Crypto · London/NY overlap — peak volume. Highest probability hour.";
    if (session.includes("New York")) return "Crypto · New York session — institutional flow. Respect macro.";
    return "Crypto · post-NY close. Lower volume. Reduce size or stand down.";
  }
  if (session.includes("Tokyo")) return "Forex · Asian session — JPY/AUD/NZD active. Majors usually range.";
  if (session.includes("London")) return "Forex · London open — highest GBP/EUR volatility window.";
  if (session.includes("Overlap")) return "Forex · London/NY overlap — your sweet spot. Best fills.";
  if (session.includes("New York")) return "Forex · NY session — USD news risk. Check the calendar.";
  return "Forex · post-NY close. Markets thinning. Wait for Asia.";
}

function pickAction(streak, lastTrade) {
  const actions = [
    "🎯 Run your D1→H4→H1 scan and build today's watchlist.",
    "🎯 Re-read your 1 favorite trade from last week. Find the pattern.",
    "🎯 Drill the 7-Q Understand checklist on your top 2 setups.",
    "🎯 Backtest 5 trades on your weakest pair. Grade each one.",
    "🎯 Review your last losing trade. What SuPER step broke?",
  ];
  if (streak.days === 0) return "🎯 Start the streak today — even a 5-minute review counts.";
  if (lastTrade?.grade === "F") return "🎯 Skip live trading today. Demo only. Rebuild discipline.";
  if (lastTrade?.grade === "A") return "🎯 Same setup, same rules. A-grade trades are repeatable.";
  // Pick deterministically by day-of-year so it's stable per day
  const idx = new Date().getUTCDate() % actions.length;
  return actions[idx];
}

function loadCachedRefresh() {
  try {
    const raw = localStorage.getItem("flexx_axel_briefing_cache");
    if (!raw) return null;
    const { date, msg } = JSON.parse(raw);
    if (date === new Date().toISOString().slice(0, 10)) return msg;
    return null;
  } catch (e) {
    return null;
  }
}

function cacheRefresh(msg) {
  try {
    localStorage.setItem(
      "flexx_axel_briefing_cache",
      JSON.stringify({ date: new Date().toISOString().slice(0, 10), msg })
    );
  } catch (e) {
    // noop
  }
}

// ── Today's Economic Flashpoints (Phase 11) ─────────────────────────
// Pulls today's high-impact (and medium, only when no high are scheduled)
// economic events from /api/market-intel/economic-calendar — the calendar
// warmer keeps that endpoint zero-burn so this section adds NO extra
// upstream load. Renders inside the briefing card itself: the first thing
// students see each morning. Auto-hides when there are no relevant events.

function EconFlashpoints() {
  const [data, setData] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(async () => {
    try {
      const token = localStorage.getItem("flexx_access_token");
      if (!token || !API) return;
      const res = await axios.get(
        `${API}/api/market-intel/economic-calendar`,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 }
      );
      setData(res.data || null);
    } catch (e) {
      // silent — non-blocking enhancement
      setData(null);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30 * 60 * 1000); // 30-min refresh
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60 * 1000); // tick countdowns
    return () => clearInterval(t);
  }, []);

  const top = useMemo(() => pickFlashpoints(data, now), [data, now]);
  if (!top || top.length === 0) return null;

  return (
    <div data-testid="axel-econ-flashpoints" style={{
      marginTop: 14,
      padding: 12,
      borderRadius: 10,
      background: "linear-gradient(180deg, rgba(201,162,39,0.06), rgba(201,162,39,0.02))",
      border: `1px solid ${THEME.gold}40`,
      borderLeft: `3px solid ${THEME.gold}`,
    }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 8, marginBottom: 10, flexWrap: "wrap",
      }}>
        <div style={{
          fontSize: 9, letterSpacing: 2.2, color: THEME.gold,
          fontFamily: "monospace", fontWeight: 800,
        }}>
          ⚡ TODAY'S ECONOMIC FLASHPOINTS
        </div>
        <div style={{
          fontSize: 9, letterSpacing: 1.5, color: THEME.mute, fontFamily: "monospace",
        }}>
          {top.length} EVENT{top.length !== 1 ? "S" : ""}
        </div>
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        {top.map((ev, i) => (
          <FlashpointRow key={`${ev.currency}-${ev.event}-${i}`} ev={ev} nowMs={now} />
        ))}
      </div>
    </div>
  );
}

function FlashpointRow({ ev, nowMs }) {
  const t = parseEventMs(ev.date);
  const diffMin = t ? Math.round((t - nowMs) / 60000) : null;
  const state = computeState(diffMin);
  const flag = currencyFlag(ev.currency);
  const timeStr = t ? new Date(t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false }) : "TBD";

  const planWithAxel = () => {
    // Pre-fill the AXEL chat with a contextual entry-planning prompt.
    // Builds a one-shot question that captures: event identity, time, and
    // status (upcoming / live / released). AXEL has live market context
    // injection on the backend so it can blend in current XAUUSD price,
    // DXY, F&G etc. without us repeating those here.
    const status = diffMin == null
      ? "scheduled today"
      : diffMin > 0
        ? `releasing in ${formatCountdown(diffMin)} (at ${timeStr} UTC)`
        : `released ${-diffMin}m ago (at ${timeStr} UTC)`;
    const prompt = `Help me plan an entry around ${ev.currency || "this"} ${ev.event || "event"} — ${status}. Walk me through the SuPER checklist for trading this catalyst, and what to watch on the spot price + DXY before/after the print. Education only.`;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("openAxel", "1");
      url.searchParams.set("axelPrompt", prompt);
      window.history.replaceState({}, "", url.toString());
      // Trigger AxelWidget's URL-param effect by re-firing a popstate-equivalent.
      // Simplest: dispatch a custom navigation that re-runs the check.
      window.dispatchEvent(new Event("popstate"));
      // Fallback: if AxelWidget hasn't mounted, hash change forces a re-eval on next mount.
    } catch (_) {
      // Last-resort: full reload with the params (still works, just less smooth).
      const u = new URL(window.location.href);
      u.searchParams.set("openAxel", "1");
      u.searchParams.set("axelPrompt", prompt);
      window.location.href = u.toString();
    }
  };

  return (
    <div data-testid="axel-flashpoint-row" style={{
      display: "grid",
      gridTemplateColumns: "auto 1fr auto auto",
      alignItems: "center",
      gap: 10,
      padding: "8px 10px",
      borderRadius: 6,
      background: "rgba(0,0,0,0.25)",
      border: `1px solid ${state.tone}30`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
        <span style={{ fontSize: 18, lineHeight: 1 }}>{flag}</span>
        <span style={{
          fontFamily: "monospace", fontSize: 10, fontWeight: 800,
          color: state.tone, letterSpacing: 1,
        }}>
          {(ev.currency || "").toUpperCase()}
        </span>
      </div>
      <div style={{ minWidth: 0 }}>
        <div data-testid="flashpoint-event" style={{
          color: THEME.text, fontSize: 12, fontWeight: 700,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {ev.event || "High-impact event"}
        </div>
        <div style={{
          color: THEME.mute, fontSize: 10, fontFamily: "monospace", marginTop: 2,
        }}>
          {timeStr} UTC · {state.cta}
        </div>
      </div>
      <div data-testid="flashpoint-countdown" style={{
        fontFamily: "monospace",
        fontSize: 11,
        fontWeight: 800,
        color: state.tone,
        textAlign: "right",
        whiteSpace: "nowrap",
        padding: "4px 8px",
        borderRadius: 4,
        background: `${state.tone}12`,
        border: `1px solid ${state.tone}40`,
      }}>
        {state.label}
      </div>
      <button
        data-testid="flashpoint-plan-axel-btn"
        onClick={planWithAxel}
        title="Plan an entry with AXEL"
        style={{
          all: "unset",
          cursor: "pointer",
          padding: "5px 9px",
          borderRadius: 4,
          border: `1px solid ${THEME.gold}55`,
          color: THEME.gold,
          fontFamily: "monospace",
          fontSize: 9,
          fontWeight: 800,
          letterSpacing: 1,
          background: `${THEME.gold}10`,
          whiteSpace: "nowrap",
        }}
      >
        🧠 PLAN
      </button>
    </div>
  );
}

function computeState(diffMin) {
  if (diffMin == null) {
    return { tone: THEME.mute, label: "TBD", cta: "Time TBD" };
  }
  if (diffMin > 60) {
    return {
      tone: THEME.gold,
      label: `IN ${formatCountdown(diffMin)}`,
      cta: "Log it · set a price-alert",
    };
  }
  if (diffMin > 0) {
    return {
      tone: "#FF8C00",
      label: `IN ${diffMin}m`,
      cta: "Stand down · volatility incoming",
    };
  }
  if (diffMin > -30) {
    return {
      tone: "#FF4444",
      label: `${-diffMin}m AGO`,
      cta: "Live volatility window",
    };
  }
  if (diffMin > -180) {
    return {
      tone: THEME.mute,
      label: `${-diffMin}m AGO`,
      cta: "Released — review the print",
    };
  }
  return {
    tone: THEME.mute,
    label: "RELEASED",
    cta: "Already printed",
  };
}

function formatCountdown(min) {
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const r = min % 60;
  return r === 0 ? `${h}h` : `${h}h ${r}m`;
}

function currencyFlag(code) {
  const map = {
    USD: "🇺🇸", EUR: "🇪🇺", GBP: "🇬🇧", JPY: "🇯🇵",
    AUD: "🇦🇺", NZD: "🇳🇿", CAD: "🇨🇦", CHF: "🇨🇭", CNY: "🇨🇳",
  };
  return map[(code || "").toUpperCase()] || "🌐";
}

function pickFlashpoints(data, nowMs) {
  if (!data || typeof data !== "object") return [];
  const high = Array.isArray(data.high) ? data.high : [];
  const medium = Array.isArray(data.medium) ? data.medium : [];

  // Always prefer high-impact. Fall back to medium only when no high events
  // exist today (keeps quiet calendar days informative without spam).
  const source = high.length > 0 ? high : medium.slice(0, 3);

  // Score each event so we prefer (a) not-yet-released, soonest first;
  // (b) recently-released within last 30m (volatility window still hot);
  // (c) drop anything older than 3h.
  const enriched = source
    .map((ev) => {
      const t = parseEventMs(ev.date);
      const diffMin = t ? Math.round((t - nowMs) / 60000) : null;
      return { ev, t, diffMin };
    })
    .filter(({ diffMin }) => {
      if (diffMin == null) return true;
      return diffMin > -180; // hide events more than 3 hours old
    })
    .sort((a, b) => {
      // Soonest upcoming first; then "just released" (negative but recent).
      const av = a.diffMin == null ? 999_999 : a.diffMin >= 0 ? a.diffMin : 100_000 + (-a.diffMin);
      const bv = b.diffMin == null ? 999_999 : b.diffMin >= 0 ? b.diffMin : 100_000 + (-b.diffMin);
      return av - bv;
    })
    .slice(0, 3)
    .map(({ ev }) => ev);

  return enriched;
}


// ── Economic red-flag banner (Phase 10 — DEPRECATED) ────────────────
// Superseded by <EconFlashpoints />. Kept as a no-op stub so any external
// references don't break; remove in a follow-up cleanup pass.
function EconRedFlagBanner() {
  return null;
}
//
//
// Below preserved temporarily for the buildEconTips helper used elsewhere.
// (No longer rendered.)
//

function buildEconTips(events, nowMs) {
  if (!Array.isArray(events) || events.length === 0) return [];
  // Keep to top 3 events to stay compact on mobile
  const sorted = events
    .slice()
    .sort((a, b) => parseEventMs(a.date) - parseEventMs(b.date))
    .slice(0, 3);
  return sorted.map((ev) => {
    const t = parseEventMs(ev.date);
    if (!t) return `${ev.event || "High-impact event"} — time TBD.`;
    const diffMin = Math.round((t - nowMs) / 60000);
    const label = [ev.currency || ev.country, ev.event].filter(Boolean).join(" · ") || "High-impact event";
    if (diffMin > 0) {
      const pretty = formatMin(diffMin);
      if (diffMin <= 90) {
        return `${label} hits in ${pretty}. Consider a price-alert instead of a live entry.`;
      }
      return `${label} in ${pretty}. Log it now so you don't get blindsided.`;
    }
    if (diffMin > -60) {
      const pretty = formatMin(-diffMin);
      return `${label} released ${pretty} ago — post-release volatility window. Wait for the dust.`;
    }
    return `${label} already released today. Review the print before the next setup.`;
  });
}

function parseEventMs(iso) {
  if (!iso) return 0;
  // FMP returns "YYYY-MM-DD HH:MM:SS" in UTC
  const s = String(iso).replace(" ", "T") + "Z";
  const d = new Date(s);
  const ms = d.getTime();
  return isNaN(ms) ? 0 : ms;
}

function formatMin(m) {
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r === 0 ? `${h}h` : `${h}h ${r}m`;
}
